import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAccount, createAccount, updateAccount } from '@/api/accounts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, BookUser, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import type { UserRole } from '@/types/account';
import { ROLE_LABELS } from '@/types/account';

export function AccountFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchAccount();
    }
  }, [id, isEdit]);

  const fetchAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAccount(parseInt(id!));
      setEmail(data.email);
      setFirstname(data.firstname);
      setLastname(data.lastname);
      setRole(data.primaryRole || '');
    } catch (err) {
      setError('Erreur lors du chargement du compte');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('L\'email est requis');
      return;
    }
    if (!firstname.trim()) {
      setError('Le prénom est requis');
      return;
    }
    if (!lastname.trim()) {
      setError('Le nom est requis');
      return;
    }
    if (!isEdit && !password) {
      setError('Le mot de passe est requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEdit) {
        await updateAccount(parseInt(id!), {
          email: email.trim(),
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          password: password || undefined,
          role: role || undefined,
        });
      } else {
        await createAccount({
          email: email.trim(),
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          password,
          role: role || undefined,
        });
      }
      navigate('/accounts');
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/accounts">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <BookUser className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {isEdit ? 'Modifier le compte' : 'Nouveau compte'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? 'Modifiez les informations du compte'
              : 'Créez un nouveau compte utilisateur'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Personal info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informations personnelles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstname">
                  Prénom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstname"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  placeholder="Ex: Jean"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastname">
                  Nom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastname"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  placeholder="Ex: Dupont"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ex: jean.dupont@example.com"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Security & Role */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sécurité et rôle</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.entries(ROLE_LABELS) as [UserRole, string][]).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Mot de passe {!isEdit && <span className="text-red-500">*</span>}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isEdit ? 'Laisser vide pour ne pas modifier' : 'Mot de passe'}
                    required={!isEdit}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {isEdit && (
                  <p className="text-xs text-muted-foreground">
                    Laissez vide pour conserver le mot de passe actuel
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/accounts')}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEdit ? 'Enregistrer' : 'Créer le compte'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
